import React, { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const COST_CATS = { GIDA: "Gıda", PERSONEL: "Personel", LOJISTIK: "Lojistik", AMBALAJ: "Ambalaj", GENEL: "Genel", EKSTRA: "Ekstra Maliyet" };
const METHODS = ["Nakit", "Havale/EFT", "Çek", "Kredi Kartı"];
const todayISO = () => new Date().toISOString().slice(0, 10);

export default function SupplierTxnForm({ open, onClose, type, suppliers, defaultId, onSaved }) {
  const isPurchase = type === "purchase";
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const sid = defaultId || suppliers[0]?.id || "";
    const sup = suppliers.find((s) => s.id === sid);
    setForm({ supplier_id: sid, amount: "", cost_category: sup?.category || "GIDA", method: "Havale/EFT", description: "", invoice_no: "", date: todayISO(), due_date: "" });
    // eslint-disable-next-line
  }, [open, type]);

  const pick = (sid) => {
    const sup = suppliers.find((s) => s.id === sid);
    setForm({ ...form, supplier_id: sid, cost_category: sup?.category || form.cost_category });
  };

  const save = async () => {
    if (!form.supplier_id || !(parseFloat(form.amount) > 0)) { toast.error("Tedarikçi ve tutar gerekli"); return; }
    setSaving(true);
    try {
      await api.post("/supplier-ledger", { ...form, type, amount: parseFloat(form.amount), due_date: form.due_date || undefined, method: isPurchase ? "" : form.method });
      toast.success(isPurchase ? "Fatura kaydedildi ve maliyete işlendi" : "Ödeme kaydedildi");
      onClose(); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="font-heading">{isPurchase ? "Alış Faturası Ekle" : "Tedarikçiye Ödeme"}</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <F label="Tedarikçi">
            <Select value={form.supplier_id || ""} onValueChange={pick}>
              <SelectTrigger data-testid="supplier-txn-supplier"><SelectValue placeholder="Seçin" /></SelectTrigger>
              <SelectContent>{suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </F>
          <div className="grid grid-cols-2 gap-4">
            <F label="Tutar (₺)"><Input type="number" value={form.amount || ""} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="supplier-txn-amount" className="font-mono" placeholder="0" /></F>
            <F label="Tarih"><Input type="date" value={form.date || ""} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="supplier-txn-date" className="font-mono" /></F>
            {isPurchase ? (
              <>
                <F label="Maliyet Kategorisi">
                  <Select value={form.cost_category || "GIDA"} onValueChange={(v) => setForm({ ...form, cost_category: v })}>
                    <SelectTrigger data-testid="supplier-txn-cost-category"><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(COST_CATS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                  </Select>
                </F>
                <F label="Fatura No"><Input value={form.invoice_no || ""} onChange={(e) => setForm({ ...form, invoice_no: e.target.value })} data-testid="supplier-txn-invoice" className="font-mono" /></F>
                <F label="Vade (boş = tedarikçi vadesi)"><Input type="date" value={form.due_date || ""} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="supplier-txn-due" className="font-mono" /></F>
              </>
            ) : (
              <F label="Ödeme Yöntemi">
                <Select value={form.method || "Havale/EFT"} onValueChange={(v) => setForm({ ...form, method: v })}>
                  <SelectTrigger data-testid="supplier-txn-method"><SelectValue /></SelectTrigger>
                  <SelectContent>{METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </F>
            )}
          </div>
          <F label="Açıklama"><Input value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="supplier-txn-description" placeholder={isPurchase ? "Örn: Et & tavuk alımı" : "Opsiyonel"} /></F>
          {isPurchase && <p className="text-xs text-muted-foreground">Fatura tutarı seçilen kategoriye maliyet olarak otomatik eklenir.</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>İptal</Button>
          <Button onClick={save} disabled={saving} data-testid="supplier-txn-save" className="gap-2">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Kaydet</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const F = ({ label, children }) => (
  <div className="space-y-2"><Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</Label>{children}</div>
);
